import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { WizardStepIndicator } from "@/components/campaigns/wizard/WizardStepIndicator";
import { StepSelectDocument } from "@/components/campaigns/wizard/StepSelectDocument";
import { StepCampaignDetails } from "@/components/campaigns/wizard/StepCampaignDetails";
import { StepRecipients } from "@/components/campaigns/wizard/StepRecipients";
import { StepReviewSend } from "@/components/campaigns/wizard/StepReviewSend";
import { EmailCampaignType } from "@/types/email";
import type { EmailDocument } from "@/types/email";

const STEPS = [
  { id: 1, label: "Email" },
  { id: 2, label: "Details" },
  { id: 3, label: "Recipients" },
  { id: 4, label: "Review" },
];

export interface WizardData {
  selectedDocument: EmailDocument | null;
  name: string;
  type: EmailCampaignType;
  subject: string;
  previewText: string;
  fromName: string;
  fromEmail: string;
  replyToEmail: string;
  selectedListIds: string[];
  estimatedRecipients: number;
  trackOpens: boolean;
  trackClicks: boolean;
}

const DEFAULT_WIZARD_DATA: WizardData = {
  selectedDocument: null,
  name: "",
  type: EmailCampaignType.Promotional,
  subject: "",
  previewText: "",
  fromName: "",
  fromEmail: "",
  replyToEmail: "",
  selectedListIds: [],
  estimatedRecipients: 0,
  trackOpens: true,
  trackClicks: true,
};

export default function CampaignWizardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedDocumentId = searchParams.get("documentId");

  const startStep = preselectedDocumentId ? 2 : 1;
  const [currentStep, setCurrentStep] = useState(startStep);
  const [data, setData] = useState<WizardData>(DEFAULT_WIZARD_DATA);

  const updateData = (partial: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...partial }));
  };

  const visibleSteps = preselectedDocumentId
    ? STEPS.filter((s) => s.id !== 1)
    : STEPS;

  return (
    <ProtectedRoute>
      <div className="lw-campaign-wizard container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate("/campaigns")}
              className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Campaigns
            </button>
            <h1 className="text-3xl font-bold text-foreground">New Campaign</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Set up your campaign in a few simple steps.
            </p>
          </div>

          {/* Step Indicator */}
          <WizardStepIndicator steps={visibleSteps} currentStep={currentStep} />

          {/* Step Content */}
          <div className="mt-8">
            {currentStep === 1 && !preselectedDocumentId && (
              <StepSelectDocument
                selected={data.selectedDocument}
                onSelect={(doc) => {
                  updateData({
                    selectedDocument: doc,
                    name: data.name || doc.name,
                  });
                  setCurrentStep(2);
                }}
              />
            )}
            {currentStep === 2 && (
              <StepCampaignDetails
                data={data}
                preselectedDocumentId={preselectedDocumentId}
                onChange={updateData}
                onBack={() => preselectedDocumentId ? navigate("/campaigns/email") : setCurrentStep(1)}
                onNext={() => setCurrentStep(3)}
              />
            )}
            {currentStep === 3 && (
              <StepRecipients
                selectedListIds={data.selectedListIds}
                estimatedRecipients={data.estimatedRecipients}
                trackOpens={data.trackOpens}
                trackClicks={data.trackClicks}
                onChange={updateData}
                onBack={() => setCurrentStep(2)}
                onNext={() => setCurrentStep(4)}
              />
            )}
            {currentStep === 4 && (
              <StepReviewSend
                data={data}
                onBack={() => setCurrentStep(3)}
                onComplete={(campaignId) => navigate(`/campaigns/${campaignId}`)}
              />
            )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}
